import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {defer, Observable} from 'rxjs';
import {finalize, share, tap} from 'rxjs/operators';

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/** How long a write that got no clean answer keeps its key, so trying again cannot run it twice. */
const RETRY_WINDOW_MS = 10 * 60 * 1000;
const WRITES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/** Keys still waiting for a clean answer survive a page reload in this tab (hashes only, no bodies). */
const STORAGE_KEY = 'lychee.idempotency.v1';
const MAX_KEPT = 50;

/**
 * One user action, one execution (see the API's docs/IDEMPOTENCY.md).
 *
 * - The same write sent again while the first is still on its way (Save pressed five times on a
 *   stuck connection, Enter pressed twice) is not sent again: every caller gets the first one's
 *   answer.
 * - Every write carries an `Idempotency-Key`. When it got no clean answer (the connection dropped,
 *   it timed out, the server failed, the page was reloaded while it hung), trying the same write
 *   again within 10 minutes reuses the key, so the server answers with the first result instead of
 *   running it again.
 *
 * "The same write" is the same method, URL and body. Uploads (FormData, files) are sent as they are.
 * Registered first, so it sees the request before the token, language and signature are added.
 */
@Injectable()
export class IdempotencyInterceptor implements HttpInterceptor {

  private readonly inFlight = new Map<string, Observable<HttpEvent<unknown>>>();
  private readonly pending = new PendingKeys();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!WRITES.has(request.method.toUpperCase()) || isExternal(request.url) || request.headers.has(IDEMPOTENCY_HEADER)) {
      return next.handle(request);
    }
    const fingerprint = fingerprintOf(request);
    if (fingerprint === null) {
      return next.handle(request);
    }
    const running = this.inFlight.get(fingerprint);
    if (running) {
      return running;
    }

    const id = hash(fingerprint);
    const shared = defer(() => {
      // Kept until a clean answer: if the page is closed or reloaded meanwhile, the next try reuses it.
      const key = this.pending.keyFor(id);
      return next.handle(request.clone({setHeaders: {[IDEMPOTENCY_HEADER]: key}}));
    }).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.pending.done(id);
          }
        },
      }),
      finalize(() => this.inFlight.delete(fingerprint)),
      share()
    );
    this.inFlight.set(fingerprint, shared);
    return shared;
  }
}

/** Keys of writes sent but not answered yet, by fingerprint hash; in sessionStorage when it works. */
class PendingKeys {
  private entries: Record<string, { key: string; until: number }> = {};

  constructor() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      this.entries = saved ? JSON.parse(saved) || {} : {};
    } catch {
      this.entries = {};
    }
  }

  keyFor(id: string): string {
    const now = Date.now();
    Object.keys(this.entries).forEach((k) => {
      if (!this.entries[k] || this.entries[k].until < now) {
        delete this.entries[k];
      }
    });
    const kept = this.entries[id];
    const key = kept ? kept.key : newKey();
    this.entries[id] = {key, until: now + RETRY_WINDOW_MS};
    const ids = Object.keys(this.entries);
    if (ids.length > MAX_KEPT) {
      ids.sort((a, b) => this.entries[a].until - this.entries[b].until)
        .slice(0, ids.length - MAX_KEPT)
        .forEach((k) => delete this.entries[k]);
    }
    this.save();
    return key;
  }

  done(id: string): void {
    delete this.entries[id];
    this.save();
  }

  private save(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
    } catch {
      // Private mode or storage full: the keys still work for this page.
    }
  }
}

/** Method, URL and body; null when the body cannot be compared (uploads). */
function fingerprintOf(request: HttpRequest<unknown>): string | null {
  const body = request.body;
  if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
    return null;
  }
  const serialized = request.serializeBody();
  if (serialized !== null && typeof serialized !== 'string') {
    return null;
  }
  return `${request.method.toUpperCase()} ${request.urlWithParams}\n${serialized ?? ''}`;
}

/** Frontend assets and absolute URLs (another host) never get the header. */
function isExternal(url: string): boolean {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return true;
  }
  return url.replace(/^\.\//, '').startsWith('assets/');
}

/** cyrb53: a 53-bit hash, so storage holds no request bodies. */
function hash(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** 34 characters of [A-Za-z0-9-], as the API accepts. */
function newKey(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return 'w-' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
